// Palmistry lore — facts, history, and reverence woven through the ritual.
// Grounded and a little woo; honoring the tradition's real, cross-cultural roots.

// Short "did you know" tidbits — rotated on the "tracing the lines" moment.
export const PALMISTRY_FACTS = [
  "Palm reading is called chiromancy — from the Greek kheir, meaning 'hand.'",
  "Its roots run to ancient India, in the Samudrika Shastra — the art of reading the body's marks.",
  "Anaxagoras practiced it in Greece; Aristotle is said to have written on the lines of the hand.",
  "The pads beneath your fingers are named for planets — Jupiter, Saturn, Apollo, Mercury.",
  "The creases on your palm begin forming around the twelfth week before you are born.",
  "Many traditions read one hand for what you were given, the other for what you've made of it.",
  "No two palms are alike — even your own left and right tell slightly different stories.",
  "Lines can soften and shift over a lifetime — the hand is a living record, not a verdict.",
  "The mystic cross at the center of the palm has been read for centuries as a sign of intuition.",
  "Romani communities carried palmistry across Europe and kept the art alive for generations.",
];

// The grounding gratitude passage — shown in the reveal, keepsake, and email.
export const HANDS_GRATITUDE = {
  title: "The hands that carried you",
  text:
    "Before this reading closes, one thing worth holding: these hands have been with you your whole life. They held on when you were brand new to the world. They've made and unmade, reached out and let go, carried what needed carrying. Whatever the lines say, your hands have already lived it — of course they'd carry the shape of your days. This was never fortune-telling. It's a few quiet minutes to commune with the part of you that has done so much, and to say thank you.",
};

// A short, respectful note on the living, cross-cultural history of the art.
export const PALMISTRY_RESPECT = {
  title: "Palmistry, with respect",
  text:
    "This art is old and shared by many peoples — the Hast Samudrika of India, the hand-lore of ancient Greece, the traditions carried by Romani communities, the readings of classical China. It has been used to reflect, to counsel, and to connect. We hold it as an invitation to notice, never a prediction, and we honor the many hands that kept it alive.",
};

// Richer entries for the browsable lore nook (/lore).
export const LORE_ENTRIES = [
  {
    key: "what",
    kicker: "the practice",
    title: "A mirror, not a map",
    body:
      "Palmistry doesn't tell you what will happen — it offers a way to look. The hand becomes a mirror: a place to slow down and notice the shape of a life, the patterns you already carry, the questions worth sitting with. Nothing here is fixed. It's a conversation with yourself, held in the open palm.",
  },
  {
    key: "origins",
    kicker: "origins",
    title: "An art many peoples kept",
    body:
      "The reading of hands is thousands of years old and belongs to no single culture. It grew in India as part of the Samudrika Shastra, was practiced in ancient Greece — where it earned the name chiromancy, from kheir, 'hand' — and traveled the trade routes into China, Persia, and Egypt. Romani communities carried and preserved it across Europe. Every reading you receive rests on all those hands.",
  },
  {
    key: "two-hands",
    kicker: "the two hands",
    title: "Given, and made",
    body:
      "In many traditions the non-dominant hand holds what you were born with — your inheritance, your raw material — while the dominant hand shows what you've done with it: the choices, the seasons, the life in motion. Read together, they're a quiet dialogue between fate and free will.",
  },
  {
    key: "living",
    kicker: "the living hand",
    title: "Lines that change",
    body:
      "The major creases begin forming around the twelfth week before birth, folded into the hand as it grows. But they aren't sealed. Fine lines appear and fade across a lifetime as you change — which is exactly why a palm is read as a living record, never a fixed sentence.",
  },
  {
    key: "mounts",
    kicker: "the mounts",
    title: "The planets in your palm",
    body:
      "The soft pads beneath each finger are named for planets — Jupiter beneath the index, Saturn the middle, Apollo the ring, Mercury the little finger — each carrying its own theme: wisdom, discipline, creativity, communication. The old readers looked to which mounts rose full or fell quiet to feel where a person's energy gathered.",
  },
  {
    key: "witch",
    kicker: "the mystic cross",
    title: "The mark of the witch",
    body:
      "An independent cross resting in the center of the palm — the quadrangle, between heart and head — has been read for centuries as a sign of the sixth sense: a natural pull toward the unseen, the intuitive, the mystical. Traditions call it the witch's mark. It names a knowing, not a power; something to trust and tend rather than prove.",
  },
  {
    key: "gratitude",
    kicker: "a way to engage",
    title: "Commune, don't consume",
    body:
      "The gentlest way into this art is with gratitude. Your hands have carried you through every moment you've lived. To read them thoughtfully — slowly, without grasping for a verdict — is a way of honoring all they've done and all they still hold. Take what resonates, leave the rest, and thank them on the way out.",
  },
];
